import React, { useMemo, useRef, useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Button from '@/components/school-management/Button/Button';
import BeatLoader from 'react-spinners/BeatLoader';
import Input from '@/components/school-management/Input/Input';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import MultiSelectDropDown, {
  MultiSelectOption,
} from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';
import styles from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import ClassModalsManager from '@/components/school-management/SchoolDetailsPage/ClassModalsManager';
import { classService, teacherService } from '@/school-management/services';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useSchoolDetails } from '@/school-management/hooks/useSchoolDetails';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import Title from '@/admin/components/Title/Title';
import ClassCodeModal from '@/components/school-management/ClassCodeModal/ClassCodeModal';
import commonUtils from '@/school-management/utils/common';
import { TeacherReportItem } from '@/school-management/types/response';
import config from '@/config';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';
import { ROUTES } from '@/admin/config/routes';
import LevelControlFields from '@/components/school-management/LevelControlFields/LevelControlFields';
import {
  LEVEL_CONTROL_DEFAULTS,
  isLevelControlValid,
} from '@/school-management/utils/levelControl';

export default function CreateClassFormPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { allowAddingStudents } = state;
  const { schoolId } = useParams();
  const { data: schoolData, isLoading: schoolDataLoading } = useSchoolDetails(schoolId);
  const { user } = useAppSelector(authSelector);
  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const teachersScopeId = organizationId || schoolId || '';
  const nextActionRef = useRef<'save' | 'uploadNow'>('save');
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [addUsersToNewGroup, setAddUsersToNewGroup] = useState(true);
  const [showUsersCreateError, setShowUsersCreateError] = useState<boolean>(false);
  const [createdGroupId, setCreatedGroupId] = useState<string>('');
  const [createdDealId, setCreatedDealId] = useState<string>('');
  const [createdClassName, setCreatedClassName] = useState<string>('');
  const [showClassCodeModal, setShowClassCodeModal] = useState(false);
  const [classCode, setClassCode] = useState<string>('');
  const [invitationUrl, setInvitationUrl] = useState<string>('');
  const [firstPageTeachers, setFirstPageTeachers] = useState<TeacherReportItem[]>([]);
  const [additionalTeachers, setAdditionalTeachers] = useState<TeacherReportItem[]>([]);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const eventDispatcherContext = useContext(EventDispatcherContext);

  const { t } = useAdminTranslation();

  // Fetch total pages info
  useEffect(() => {
    const fetchFirstPageInfo = async () => {
      if (!teachersScopeId) return;
      setIsLoadingFirstPage(true);
      try {
        const response = await teacherService.getAllTeachers(teachersScopeId, { page: 1 });
        setTotalPages(response.data?.totalPages ?? 1);
        setFirstPageTeachers(response.data?.items ?? []);
      } catch (error) {
        console.error('Error fetching teachers page info:', error);
        setFirstPageTeachers([]);
      } finally {
        setIsLoadingFirstPage(false);
      }
    };
    fetchFirstPageInfo();
  }, [teachersScopeId]);

  const allTeachers = useMemo(() => {
    const teachersMap = new Map<string, TeacherReportItem>();

    (firstPageTeachers || []).forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    additionalTeachers.forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    return Array.from(teachersMap.values());
  }, [firstPageTeachers, additionalTeachers]);

  const handleLoadMore = useCallback(async () => {
    if (!teachersScopeId || isLoadingMore) return;

    let nextPage = 1;
    for (let i = 2; i <= totalPages; i++) {
      if (!loadedPages.has(i)) {
        nextPage = i;
        break;
      }
    }

    if (nextPage === 1 || nextPage > totalPages) return;

    setIsLoadingMore(true);
    try {
      const response = await teacherService.getAllTeachers(teachersScopeId, { page: nextPage });
      if (response.data?.items) {
        setAdditionalTeachers(prev => {
          const existingIds = new Set([
            ...prev.map(t => t.userId),
            ...(firstPageTeachers || []).map(t => t.userId),
          ]);
          const newTeachers = response.data.items.filter(
            (t: TeacherReportItem) => !existingIds.has(t.userId)
          );
          return [...prev, ...newTeachers];
        });
        setLoadedPages(prev => new Set([...prev, nextPage]));
      }
    } catch (error) {
      console.error('Error loading more teachers:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [teachersScopeId, loadedPages, totalPages, isLoadingMore, firstPageTeachers]);

  const hasMoreTeachers = loadedPages.size < totalPages;

  const gradeOptions = useMemo(() => {
    return commonUtils.getAllGradesForDropdown(t).map(grade => ({
      label: grade.label,
      value: grade.id,
    }));
  }, [t]);

  const schoolName = user?.metadata?.organizationDetails?.organizationName;
  const { control, handleSubmit, formState, watch, setValue } = useForm({
    mode: 'onTouched',
    defaultValues: {
      className: '',
      grade: null as Option | null,
      teachers: [] as MultiSelectOption[],
      ...LEVEL_CONTROL_DEFAULTS,
    },
  });

  const formValues = watch();

  // Check if form is valid for enabling buttons
  const isFormValid =
    formValues.className?.trim() &&
    formValues.grade &&
    formValues.teachers?.length > 0 &&
    isLevelControlValid(formValues);

  const onBack = async () => {
    const organizationId =
      user?.metadata?.organizationDetails?.organizationId || (schoolId as string) || '';

    if (organizationId && createdGroupId) {
      const resp = await classService.deleteClass(organizationId, createdGroupId);
    }

    navigate(-1);
  };

  const onSubmit = async (values: any) => {
    if (isCreating) {
      return;
    }

    const organizationId =
      user?.metadata?.organizationDetails?.organizationId || (schoolId as string) || '';

    const listOfTeachers: string[] = Array.isArray(values.teachers)
      ? (values.teachers as any[])
          .map(t => (t && typeof t === 'object' ? t.value : t))
          .map(v => String(v))
          .filter(Boolean)
      : [];

    const selectedGradeId = String(values.grade?.value || '');
    const className = String(values.className || '').trim();
    const levelControlValues = {
      placementTestRequired: Boolean(values.placementTestRequired),
      defaultStartingLevel: values.placementTestRequired
        ? null
        : values.defaultStartingLevel ?? null,
      allowIndividualAdvancement: Boolean(values.allowIndividualAdvancement),
    };

    setIsCreating(true);

    try {
      setCreatedClassName(className);

      if (createdGroupId && createdDealId) {
        const resp = await classService.updateClass(organizationId, createdGroupId, {
          teacherIds: listOfTeachers,
          groupName: className,
          gradeId: selectedGradeId,
          ...levelControlValues,
        });
      } else {
        const resp = await classService.createClass(organizationId, {
          listOfTeachers,
          groupName: className,
          gradeId: selectedGradeId,
          ...levelControlValues,
        });
        const isSuccess = resp?.data?.status === 'success';

        const respData = (resp?.data?.data || {}) as any;
        setCreatedGroupId(String(respData?.groupId || ''));
        setCreatedDealId(String(respData?.dealId || ''));

        if (respData?.classCode) {
          setClassCode(respData.classCode);
          setInvitationUrl(respData?.invitationUrl || respData?.invitationLink || '');

          // Wait 2 seconds for DynamoDB stream to update postgres
          await new Promise(resolve => setTimeout(resolve, 2000));
          setShowClassCodeModal(true);
          return;
        }
      }

      if (nextActionRef.current === 'uploadNow') {
        // Wait 2 seconds for DynamoDB stream to update postgres
        await new Promise(resolve => setTimeout(resolve, 2000));
        setShowUploadModal(true);
      } else {
        // Wait 2 seconds for DynamoDB stream to update postgres
        await new Promise(resolve => setTimeout(resolve, 2000));
        eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
        navigate(`/schools/${organizationId}?tab=classes`, {
          replace: true,
          state: { notification: { message: t('dynamicForm.notification.classCreated') } },
        });
      }
    } catch (e: any) {
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      setNotifType('error');
      setNotifOpen(true);
    }

    try {
      setIsCreating(false);
    } catch (e) {}
  };

  const isClassCodeEnabled = useMemo(() => {
    return !!schoolData?.data?.data?.school?.classCodeEnabled;
  }, [schoolData]);

  const teachersOptions = useMemo(() => {
    return (
      allTeachers?.map(t => ({
        label: `${t.firstName} ${t.lastName}`,
        value: t.userId,
        data: { email: t.email },
      })) || []
    );
  }, [allTeachers]);

  if (isLoadingFirstPage || schoolDataLoading) {
    return (
      <div className={styles.schoolDetailsPage}>
        <div style={{ textAlign: 'center', padding: '40px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.createClassPage}>
      <button
        className={`flex gap-2 items-center ${styles.backBtn}`}
        onClick={onBack}
        disabled={isCreating}
      >
        <AppIcon icon='ArrowLeft' width={16} height={16} />
        {t('common.back')}
      </button>

      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('schoolDetailsPage.classes.newClass')}</h1>
        <span className={styles.schoolName}>{schoolName}</span>
      </div>

      <div className={styles.formCard}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formFields}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t('schoolDetailsPage.classes.className')} *
              </label>
              <Controller
                name='className'
                control={control}
                rules={{
                  required: true,
                  maxLength: {
                    value: 40,
                    message: t('dynamicForm.validation.maxLength', { length: 40 }),
                  },
                  pattern: {
                    value: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
                    message: t('editClassInvalidClassNameFormat'),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    maxLength={config.classNameMaxLength}
                    placeholder={t('schoolDetailsPage.classes.className') || ''}
                    className={styles.formInput}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t('schoolDetailsPage.classes.gradeLabel')} *
              </label>
              <Controller
                name='grade'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DropDown
                    {...field}
                    autoClose={true}
                    placeholder={t('schoolDetailsPage.classes.gradeLabel') || ''}
                    options={gradeOptions}
                    value={field.value || undefined}
                    onChange={(value: Option) => field.onChange(value)}
                  />
                )}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t('schoolDetailsPage.classes.teachersLabel')} *
              </label>
              <Controller
                name='teachers'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <MultiSelectDropDown
                    {...field}
                    placeholder={t('schoolDetailsPage.classes.teachersPlaceholder') || ''}
                    searchPlaceholder={
                      t('schoolDetailsPage.classes.teachersSearchPlaceholder') || ''
                    }
                    options={teachersOptions}
                    value={field.value}
                    onChange={(value: MultiSelectOption[]) => field.onChange(value)}
                    maxSelections={2}
                    maxHeight='500px'
                    onLoadMore={handleLoadMore}
                    hasMore={hasMoreTeachers}
                    isLoadingMore={isLoadingMore}
                    enableAutocomplete={true}
                    organizationId={teachersScopeId}
                    autocompleteType='teachers'
                    autocompleteStartOptions={teachersOptions}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <LevelControlFields
              placementTestRequired={Boolean(formValues.placementTestRequired)}
              defaultStartingLevel={formValues.defaultStartingLevel ?? null}
              allowIndividualAdvancement={Boolean(formValues.allowIndividualAdvancement)}
              isNewClass
              onChange={next => {
                setValue('placementTestRequired', next.placementTestRequired, {
                  shouldDirty: true,
                });
                setValue('defaultStartingLevel', next.defaultStartingLevel, { shouldDirty: true });
                setValue('allowIndividualAdvancement', next.allowIndividualAdvancement, {
                  shouldDirty: true,
                });
              }}
            />
          </div>

          <div className={styles.actionButtons}>
            <button
              type='button'
              className={styles.cancelButton}
              onClick={onBack}
              disabled={isCreating}
            >
              {t('common.cancel')}
            </button>
            <div className={styles.rightButtons}>
              {!isClassCodeEnabled && (
                <button
                  type='button'
                  className={styles.saveButton}
                  disabled={!isFormValid || isCreating}
                  onClick={() => {
                    nextActionRef.current = 'save';
                    handleSubmit(onSubmit)();
                  }}
                >
                  {isCreating && nextActionRef.current === 'save' ? (
                    <BeatLoader />
                  ) : (
                    t('schoolDetailsPage.classes.saveClassAndAddLater')
                  )}
                </button>
              )}
              {!isClassCodeEnabled && allowAddingStudents && (
                <button
                  type='button'
                  className={styles.continueButton}
                  disabled={!isFormValid || isCreating}
                  onClick={() => {
                    nextActionRef.current = 'uploadNow';
                    handleSubmit(onSubmit)();
                  }}
                >
                  {t('schoolDetailsPage.classes.continueToUploadingStudents')}
                  {isCreating && nextActionRef.current === 'uploadNow' && (
                    <div>
                      <BeatLoader />
                    </div>
                  )}
                </button>
              )}
              {isClassCodeEnabled && (
                <button
                  type='button'
                  className={styles.continueButton}
                  disabled={!isFormValid || isCreating}
                  onClick={() => {
                    nextActionRef.current = 'uploadNow';
                    handleSubmit(onSubmit)();
                  }}
                >
                  {t('schoolDetailsPage.classes.create')}
                  {isCreating && nextActionRef.current === 'uploadNow' && (
                    <div>
                      <BeatLoader />
                    </div>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <ClassCodeModal
        open={showClassCodeModal}
        classCode={classCode}
        invitationUrl={invitationUrl}
        onClose={() => {
          setShowClassCodeModal(false);
          navigate(`/schools/${schoolId}?tab=classes`, { replace: true });
        }}
      />
      <ClassModalsManager
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        addUsersToNewEntity={addUsersToNewGroup}
        setAddUsersToNewEntity={setAddUsersToNewGroup}
        onUsersCreate={async () => {
          setShowUploadModal(false);
          navigate(`/schools/${schoolId}?tab=classes`, { replace: true });
        }}
        setShowUsersCreateError={setShowUsersCreateError}
        groupId={createdGroupId}
        groupName={createdClassName}
        dealId={createdDealId}
        isTeacher={false}
        placementTestRequired={Boolean(formValues.placementTestRequired)}
        onStayPageStudentAddComplete={() => {
          eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
          navigate(ROUTES.VIEW_CLASS(schoolId || '', createdGroupId), { replace: true });
        }}
      />
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => {
          setNotifOpen(false);
        }}
      />
    </div>
  );
}
